import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send, Shield, Radio, User } from "lucide-react";

const ROLE_CHIP = {
  admin: "text-[#FF007F] border-[#FF007F]",
  official: "text-[#B084FF] border-[#B084FF]",
  streamer: "text-[#F4D03F] border-[#F4D03F]",
  member: "text-gray-400 border-[#232330]",
};

const ROLE_ICON = { admin: Shield, official: Shield, streamer: Radio };

export default function FixtureChat({ fixture, teams = [], open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const scrollRef = useRef(null);

  const home = teams.find((t) => t.id === fixture?.home_team_id);
  const away = teams.find((t) => t.id === fixture?.away_team_id);

  const load = async () => {
    if (!fixture?.id) return;
    try {
      const { data } = await api.get(`/fixtures/${fixture.id}/chat`);
      setMessages(data || []);
      setError("");
      setTimeout(() => scrollRef.current?.scrollTo({ top: 1e6, behavior: "smooth" }), 50);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load chat");
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [open, fixture?.id]);

  const send = async () => {
    const msg = draft.trim();
    if (!msg) return;
    setBusy(true);
    try {
      await api.post(`/fixtures/${fixture.id}/chat`, { message: msg });
      setDraft("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#B084FF]" />
            Match Chat · {home?.tag || "TBD"} vs {away?.tag || "TBD"}
          </DialogTitle>
        </DialogHeader>
        <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 -mt-2">
          {fixture?.scheduled_at ? new Date(fixture.scheduled_at).toLocaleString() : ""} · Team reps, officials & admins only
        </div>
        {error && (
          <div className="border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-400">{error}</div>
        )}
        <div
          ref={scrollRef}
          data-testid="fixture-chat-body"
          className="h-72 overflow-y-auto border border-[#232330] bg-[#05050A] p-3 space-y-3"
        >
          {messages.length === 0 && !error && (
            <div className="text-center text-[10px] font-mono uppercase tracking-widest text-gray-600 pt-8">
              No messages yet — start the conversation.
            </div>
          )}
          {messages.map((m) => {
            const Icon = ROLE_ICON[m.author_role] || User;
            return (
              <div key={m.id} data-testid={`chat-msg-${m.id}`} className="space-y-1">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
                  <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 ${ROLE_CHIP[m.author_role] || ROLE_CHIP.member}`}>
                    <Icon className="w-2.5 h-2.5" />
                    {(m.author_role || "member").toUpperCase()}
                  </span>
                  <span className="text-white/90 font-heading tracking-tight">{m.author_name || m.author_email}</span>
                  <span className="text-gray-600 ml-auto">
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div className="text-sm text-gray-200 leading-relaxed whitespace-pre-wrap pl-1">{m.message}</div>
              </div>
            );
          })}
        </div>
        {!error && (
          <div className="flex gap-2 pt-1">
            <Input
              data-testid="fixture-chat-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Type a message — enter to send"
              className="rounded-none bg-[#05050A] border-[#232330] font-mono text-sm"
            />
            <Button
              onClick={send}
              disabled={busy || !draft.trim()}
              data-testid="fixture-chat-send"
              className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest text-xs font-bold px-4"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
